import { isPlatformBrowser } from '@angular/common';
import {
  provideHttpClient,
  withFetch,
  withInterceptors,
} from '@angular/common/http';
import {
  ApplicationConfig,
  DEFAULT_CURRENCY_CODE,
  importProvidersFrom,
  inject,
  LOCALE_ID,
  PLATFORM_ID,
  provideAppInitializer,
  provideZoneChangeDetection,
  REQUEST,
} from '@angular/core';
import {
  FirebaseApp,
  initializeApp,
  initializeServerApp,
  provideFirebaseApp,
} from '@angular/fire/app';
import { getAuth, provideAuth } from '@angular/fire/auth';
import { provideFirestore } from '@angular/fire/firestore';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  provideRouter,
  withComponentInputBinding,
  withInMemoryScrolling,
} from '@angular/router';
import { provideTransloco, TranslocoService } from '@ngneat/transloco';
import { provideTranslocoMessageformat } from '@ngneat/transloco-messageformat';
import { getFirestore } from 'firebase/firestore';
import { NgxMapboxGLModule } from 'ngx-mapbox-gl';
import { provideMarkdown } from 'ngx-markdown';
import { ConfirmationService, MessageService } from 'primeng/api';
import { firstValueFrom } from 'rxjs';
import { environment } from '../environments/environment';
import { appRoutes } from './app.routes';
import { languageInterceptor } from './core/api/interceptors/language/language.interceptor';
import { maintenanceInterceptor } from './core/api/interceptors/maintenance.interceptor';
import { authInterceptor } from './core/auth/auth.interceptor';
import { TranslocoHttpLoader } from './core/transloco/transloco.http-loader';

export const appConfig: ApplicationConfig = {
  providers: [
    ConfirmationService,
    MessageService,
    provideAnimations(),
    provideHttpClient(
      withInterceptors([
        authInterceptor,
        languageInterceptor,
        maintenanceInterceptor,
      ]),
      withFetch(),
    ),
    provideMarkdown(),

    // Transloco Config
    provideTransloco({
      config: {
        availableLangs: [
          {
            id: 'it',
            label: 'Italiano',
          },
          {
            id: 'en',
            label: 'English',
          },
          {
            id: 'fr',
            label: 'Francese',
          },
          {
            id: 'es',
            label: 'Spagnolo',
          },
        ],
        defaultLang:
          typeof window !== 'undefined' && window.localStorage
            ? localStorage.getItem('language') || 'it'
            : 'it',
        fallbackLang: 'it',
        reRenderOnLangChange: true,
        prodMode: true,
      },
      loader: TranslocoHttpLoader,
    }),

    provideTranslocoMessageformat(),
    provideAppInitializer(() => {
      const initializerFn = (() => {
        const translocoService = inject(TranslocoService);
        const defaultLang = translocoService.getDefaultLang();
        translocoService.setActiveLang(defaultLang);

        return () => firstValueFrom(translocoService.load(defaultLang));
      })();
      return initializerFn();
    }),

    provideClientHydration(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(
      appRoutes,
      withComponentInputBinding(),
      withInMemoryScrolling({
        scrollPositionRestoration: 'enabled',
        anchorScrolling: 'enabled',
      }),
    ),
    provideFirebaseApp(() => {
      if (isPlatformBrowser(inject(PLATFORM_ID))) {
        return initializeApp(environment.firebaseConfig);
      }

      // Optional, since it's null in dev-mode and SSG
      const request = inject(REQUEST, { optional: true });
      const authIdToken = request?.headers
        .get('authorization')
        ?.split('Bearer ')[1];
      return initializeServerApp(environment.firebaseConfig, {
        authIdToken,
        releaseOnDeref: request || undefined,
      });
    }),
    provideAuth(() => getAuth(inject(FirebaseApp))),
    provideFirestore(() => getFirestore(inject(FirebaseApp))),

    { provide: LOCALE_ID, useValue: 'it' },
    { provide: DEFAULT_CURRENCY_CODE, useValue: 'EUR' },

    importProvidersFrom(
      NgxMapboxGLModule.withConfig({
        accessToken:
          'pk.eyJ1IjoibHVjYS1iZWxsZWdvbmkiLCJhIjoiY20xdDE5enk3MDB3NDJrczhvZThlam5tZSJ9.fTtKh3zEg1bpAj_D1O5p1Q', // Optional, can also be set per map (jwtToken input of mgl-map)
      }),
    ),
  ],
};
